import {
  CHAT_UPLOAD_MAX_IMAGE_FILE_BYTES,
  classifyChatUploadFile,
} from './chatUploadLimits';
import {
  loadImageElement,
  prepareChatImageFileForUpload,
} from './chatImagePrepare';

function isProbablyImageFile(file) {
  if (!file || file.size <= 0) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(file.name || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp)$/i.test(name) || name === 'image.png' || name === 'blob';
}

function normalizeClipboardImageFile(file) {
  const mime = String(file.type || '').toLowerCase();
  const resolvedMime = mime.startsWith('image/') ? mime : 'image/png';
  let name = String(file.name || '').trim();
  if (!name || name === 'blob') {
    const ext = resolvedMime === 'image/jpeg' ? 'jpg' : (resolvedMime.split('/')[1] || 'png');
    name = `clipboard-${Date.now()}.${ext}`;
  }
  if (file.type) return file;
  return new File([file], name, { type: resolvedMime, lastModified: file.lastModified || Date.now() });
}

/**
 * Nén ảnh clipboard (screenshot) nếu vượt giới hạn chat.
 * @returns {Promise<File|null>} null nếu không nén được đủ nhỏ.
 */
export async function compressClipboardImageToMaxBytes(
  file,
  maxBytes = CHAT_UPLOAD_MAX_IMAGE_FILE_BYTES,
) {
  if (!file || file.size <= maxBytes) return file;
  if (classifyChatUploadFile(file) !== 'image') return file;

  const resized = await prepareChatImageFileForUpload(file, { maxBytes });
  if (resized && resized.size <= maxBytes) return resized;

  try {
    const img = await loadImageElement(file);
    let scale = Math.min(1, Math.sqrt(maxBytes / file.size) * 0.95);
    let quality = 0.9;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const width = Math.max(1, Math.floor(img.width * scale));
      const height = Math.max(1, Math.floor(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) break;
      ctx.drawImage(img, 0, 0, width, height);

      // eslint-disable-next-line no-await-in-loop
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });
      if (blob && blob.size > 0 && blob.size <= maxBytes) {
        const baseName = String(file.name || 'clipboard.png').replace(/\.[^.]+$/, '') || 'clipboard';
        return new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }

      scale *= 0.8;
      quality = Math.max(0.55, quality - 0.05);
    }
  } catch (e) {
    void e;
  }

  return null;
}

/**
 * Lấy ảnh từ clipboard (Print Screen / copy ảnh).
 * Hỗ trợ cả clipboardData.items (Chrome) và clipboardData.files.
 */
export function extractImageFilesFromClipboardEvent(event) {
  const clipboard = event && event.clipboardData;
  if (!clipboard) return [];

  const out = [];
  const seen = new Set();

  const pushFile = (raw) => {
    if (!raw || !(raw instanceof File) || raw.size <= 0 || !isProbablyImageFile(raw)) return;
    const file = normalizeClipboardImageFile(raw);
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(file);
  };

  if (clipboard.items && clipboard.items.length) {
    for (let i = 0; i < clipboard.items.length; i += 1) {
      const item = clipboard.items[i];
      if (!item || item.kind !== 'file') continue;
      const type = String(item.type || '').toLowerCase();
      if (!type.startsWith('image/')) continue;
      pushFile(item.getAsFile());
    }
  }

  if (!out.length && clipboard.files && clipboard.files.length) {
    for (let i = 0; i < clipboard.files.length; i += 1) {
      pushFile(clipboard.files[i]);
    }
  }

  return out;
}

export async function prepareClipboardImagesForUpload(event) {
  const files = extractImageFilesFromClipboardEvent(event);
  if (!files.length) return [];

  const prepared = [];
  for (let i = 0; i < files.length; i += 1) {
    let file = files[i];
    file = await prepareChatImageFileForUpload(file);
    if (file.size > CHAT_UPLOAD_MAX_IMAGE_FILE_BYTES) {
      const compressed = await compressClipboardImageToMaxBytes(file);
      if (compressed) {
        file = compressed;
      }
    }
    prepared.push(file);
  }
  return prepared;
}
